import React, {
  useCallback, useEffect, useState, useMemo, Dispatch, SetStateAction,
} from 'react';
import {
  Message, Input, Dropdown, Button, Loader,
} from 'semantic-ui-react';
import {
  Other,
} from 'simplydo/interfaces';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import useTheme from 'theme/useTheme';
import util from 'utils/utils';
import api from 'api';

import { useLocation, useHistory } from 'react-router-dom';

import TagDropdownChooser from 'components/lib/Choosers/Tags/TagDropdownChooser';
import {
  IProjectGeneralProps, IProjectLaneProps, IProjectIdeaProps, IProjectAssigneeProps, ProjectSelectionModes,
} from '.';
import CustomLaneCard from './CustomLaneCard';
import CustomLaneHeader from './CustomLaneHeader';
import BoardEvents from './BoardEvents';
import Board, { IIdeaCard, ILaneData } from './Board';

const StyledProjectBoardContainer = styled.div`
  // The height of the board must be the viewports height minus the top bars
  height: calc(100vh - 205px);
  display: flex;
  flex-direction: column;
`;

const StyledBoard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 10px;
  > .row-flex {
    display: flex;
    align-items: center;
  }
  margin-top: -10px;
  flex-wrap: wrap;
  h1, h2, h3, h4, h5 {
    font-family: 'Inter';
  }
  .ui.message {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    font-family: 'Inter';
    width: 40%;
    margin: 0 0 0 20px;
    padding: 0.5em 1em;
  }
`;

const StyledLaneContainer = styled.div`
  min-height: 200px;
  flex-grow: 1;
  max-height: 100%;
  font-family: 'Inter';
  h1, h2, h3, h4, h5 {
    font-family: 'Inter';
  }
  > p {
    margin-top: 20px;
    text-align: center;
    font-size: 16px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 10px;
  > span {
    display: block;
    margin: 0 0 0 5px;
    font-weight: bold;
  }
`;

const FilterHeight = '35px';

const StyledBoardHeader = styled.div`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isComputer ? 'row' : 'column')};
  > .filter-container {
    display: flex;
    flex-direction: ${({ theme }) => (theme.sizes.isComputer ? 'row' : 'column')};
  }
  .header {
    display: flex;
    flex-direction: row;
    align-items: center;
    ${({ theme }) => (!theme.sizes.isComputer ? `
      margin-bottom: 5px;
    ` : '')}
    height: ${FilterHeight};
  }
  .filters {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    flex: 1;
    margin-top: 10px;
    height: ${FilterHeight};
  }
  ${({ theme }) => (!theme.sizes.isComputer ? `
    gap: 5px;
  ` : `
    flex-wrap: wrap;
    justify-content: space-between;
    margin: -3px 0 0 0;
    > * {
      margin: 3px;
      &:last-child {
        margin-right: 0;
      }
    }
  `)}
  ${({ theme }) => (!theme.sizes.isMobile ? `
    .header {
      flex: 0;
      min-width: fit-content;
    }
  ` : `
    .header {
      margin-bottom: 5px;
    }
  `)}
`;

// const StyledProjectBoard = styled(Board)`
// background: none;
// /* .smooth-dnd-container.vertical > .smooth-dnd-draggable-wrapper{
//   overflow: visible;
// }
// .imFibB{
//   overflow: visible;
// } */
// `;

type IProjectBoardProps = IProjectGeneralProps & IProjectLaneProps & IProjectIdeaProps & IProjectAssigneeProps & {
  setNewLaneName: Dispatch<SetStateAction<string>>,
  setLaneOpen: Dispatch<SetStateAction<boolean>>,
  setNewLaneDescription: Dispatch<SetStateAction<string>>,
  cardClicked: (ideaId: string) => void,
  loading?: boolean,
  initialDataLoaded?: boolean,
  setCurrentIdeaId: Dispatch<SetStateAction<string>>,
  highlightCard?: string,
  setLaneIdeaOrder: Dispatch<SetStateAction<string[]>>,
  heading?: string,
  updateIdeaPosition: (ideaId: string, targetLaneId: string, position: number) => void,
  onIdeaActivity: (ideaId: string) => void,
  rewardIdeaId: string,
  setRewardIdeaId: Dispatch<SetStateAction<string>>,
  setSetupModalOpen: Dispatch<SetStateAction<boolean>>,
  laneOpen: boolean,
  newLaneName: string,
  newLaneDescription: string,
  newLaneNameSaved: boolean,
  newLaneDescriptionSaved: boolean,
  setNewLaneNameSaved: Dispatch<SetStateAction<boolean>>,
  setNewLaneDescriptionSaved: Dispatch<SetStateAction<boolean>>,
  updateIdeaPositionMulti: (originalLane: string, targetLane: string) => void,

  selectionMode?: ProjectSelectionModes,
  selectedIdeaIds: string[],
  setSelectedIdeaIds: Dispatch<SetStateAction<string[]>>,
}


export type IHeaderImpactMeasure = (Other.IImpact & { laneTotalImpactValue?: number, laneDropdownImpactValues?: { [key: string]: number } });

const ProjectBoard = (props: IProjectBoardProps) => {
  const {
    setNewLaneName,
    setLaneOpen,
    setCurrentLane,
    defaultLane,
    setNewLaneDescription,
    user,
    setLanes,
    lanes,
    ideas,
    usingPropIdeas,
    ideasTotal,
    loading,
    initialDataLoaded,
    setCurrentIdeaId,
    highlightCard,
    handleWebsocketLanes,
    setLaneIdeaOrder,
    canManage,
    getBoard,
    forId,
    isChallengeBoard,
    updateIdeaPosition,
    rewardIdeaId,
    setRewardIdeaId,
    setSetupModalOpen,
    availableTags,
    selectionMode,
    selectedIdeaIds,
    setSelectedIdeaIds,
  } = props;
  const [laneData, setLaneData] = useState<ILaneData[]>([]);
  const [challenge, setChallenge] = useState<Other.IChallenge | null>(null);
  const [laneImpactMeasures, setLaneImpactMeasures] = useState<{
    [laneId: string]: IHeaderImpactMeasure[]
  }>({});
  const totalIdeas = ideas?.length || 0;
  const theme = useTheme();

  const history = useHistory();
  const location = useLocation();
  const { search } = location;
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const ideaFilter = useMemo(() => queryParams.get('ideaFilter') || '', [queryParams]);
  const ideaView = useMemo(() => queryParams.get('ideaView') || '', [queryParams]);
  const queryTags = useMemo(() => queryParams.get('tags') || '', [queryParams]);
  const queryTagsArray = useMemo(() => queryTags.split(','), [queryTags]);

  const selectedTags = useMemo(() => (isChallengeBoard && challenge && challenge.tagList?.filter((tag) => queryTagsArray.includes(tag._id))) || [], [queryTagsArray, challenge, isChallengeBoard]);

  const isMultiSelect = useMemo(() => selectionMode === 'multi', [selectionMode]);

  const onSelectIdeas = useCallback((ideaIds: string[], toSelect: boolean) => {
    if (toSelect) {
      setSelectedIdeaIds((prev) => [...prev, ...ideaIds]);
    } else {
      setSelectedIdeaIds((prev) => prev.filter((ideaId) => !ideaIds.includes(ideaId)));
    }
  }, [setSelectedIdeaIds]);

  const removeTag = useCallback((tag) => {
    if (!queryTagsArray.includes(tag._id)) {
      return;
    }
    queryTagsArray.splice(queryTagsArray.indexOf(tag._id), 1);
    queryParams.set('tags', queryTagsArray.join(','));
    history.push({ ...location, search: queryParams.toString() });
  }, [queryTagsArray, queryParams, history, location]);

  const addTag = useCallback((tag) => {
    if (queryTagsArray.includes(tag._id)) {
      return;
    }
    queryTagsArray.push(tag._id);
    queryParams.set('tags', queryTagsArray.join(','));
    history.push({ ...location, search: queryParams.toString() });
  }, [queryTagsArray, queryParams, history, location]);

  const setIdeaFilter = useCallback((filter) => {
    if (filter.length === 0) {
      queryParams.delete('ideaFilter');
    } else {
      queryParams.set('ideaFilter', filter);
    }
    history.push({ ...location, search: queryParams.toString() });
  }, [history, queryParams, location]);

  const setIdeaView = useCallback((view) => {
    queryParams.set('ideaView', view);
    history.push({ ...location, search: queryParams.toString() });
  }, [history, queryParams, location]);

  const getLaneData = useCallback(() => {
    const cards: IIdeaCard[] = ideas?.map((idea) => {
      const board = getBoard(idea);
      const ret: IIdeaCard = {
        id: idea._id, idea, lane: board?.lane, position: board?.order || 0,
      };
      if (board?.lane) {
        const cardLane = lanes.find((l) => l._id === board.lane);
        ret.laneMetaType = (cardLane?.metaType || 'normal');
      }
      if (!canManage && !(board?.assignees?.indexOf(user._id) > -1)) {
        ret.draggingDisabled = true;
      }
      return ret;
    }).sort((a, b) => (a.position > b.position ? 1 : -1));

    const newLanes: ILaneData[] = [{
      id: 'default',
      title: defaultLane?.name || 'Default',
      description: defaultLane?.description || '',
      label: 'Default',
      cards: cards.filter((i) => !i.lane),
      draggingDisabled: true,
    }];
    lanes.forEach((l) => {
      newLanes.push({
        id: l._id,
        title: l.name,
        description: l.description,
        metaType: l.metaType,
        cards: cards.filter((i) => i.lane === l._id),
      });
    });
    const ideaOrder: string[] = [];
    newLanes.forEach((lane) => {
      const { cards: newCards = [] } = lane;
      newCards.forEach((card) => {
        const { idea } = card;
        ideaOrder.push(idea._id);
      });
    });
    setLaneIdeaOrder(ideaOrder);
    setLaneData(newLanes);
  }, [getBoard, setLaneIdeaOrder, ideas, user, lanes, canManage, defaultLane]);

  useEffect(() => {
    getLaneData();
  }, [getLaneData]);

  useEffect(() => {
    if (forId && isChallengeBoard) {
      api.challenges.get(forId, (c) => {
        setChallenge({
          ...c,
          collaborators: c.collaborators || [],
        });
      }, () => { });
    }
  }, [forId, isChallengeBoard]);

  const createLane = useCallback(() => {
    const unnamedLanes = lanes.filter((l) => (l.name || '').indexOf('New lane ') > -1);
    api.boards.createProjectLane(forId, { name: `New lane ${unnamedLanes.length + 1}` }, (lane) => {
      const newLanes = Object.assign([], lanes);
      newLanes.push(lane);
      setLanes(newLanes);
      handleWebsocketLanes(newLanes);
      setNewLaneName('');
      setNewLaneDescription('');
      setCurrentLane(lane);
    }, (err) => toast.error(err.message));
  }, [forId, setLanes, handleWebsocketLanes, lanes, setCurrentLane, setNewLaneName, setNewLaneDescription]);

  const cardDragged = useCallback((event) => {
    if (!event.destination) {
      return;
    }
    if (event.type === 'card') {
      updateIdeaPosition(event.draggableId.split('-')[1], event.destination.droppableId.split('-')[1], event.destination.index);
    } else if (event.type === 'lane') {
      if (event.source.index === 0) {
        toast.error("Can't move default lane");
        return;
      }
      const actualTargetIndex = (event.destination.index - 1);
      const actualSourceIndex = (event.source.index - 1);

      const selectedLane = lanes[actualSourceIndex];
      // Create new lanes list
      const prevLanes = Object.assign([], lanes);
      const newLanes = util.moveArrayItem(lanes, actualSourceIndex, actualTargetIndex);

      setLanes(newLanes);
      // Better UX if we update before API call, then reset if it fails
      api.boards.updateProjectLane(forId, selectedLane._id, { order: actualTargetIndex }, () => {
        handleWebsocketLanes(newLanes);
      }, () => {
        toast.error('Failed to move lane');
        setLanes(prevLanes);
      });
    }
  }, [updateIdeaPosition, lanes, setLanes, forId, handleWebsocketLanes]);

  const laneClicked = useCallback((laneId) => {
    setNewLaneName('');
    setLaneOpen(true);
    if (laneId === 'default') {
      setCurrentLane(defaultLane);
      setNewLaneDescription(defaultLane?.description || '');
    } else {
      const newLane = lanes.filter((l) => l._id === laneId)[0];
      setCurrentLane(newLane);
      setNewLaneDescription(newLane?.description || '');
    }
  }, [setNewLaneName, setLaneOpen, setCurrentLane, setNewLaneDescription, lanes, defaultLane]);

  const getLaneImpactMeasures = useCallback(() => {
    api.boards.getProjectLaneImpactMeasures(forId, (data) => {
      setLaneImpactMeasures(data.laneImpactMeasures);
    }, () => {});
  }, [forId]);

  useEffect(() => {
    if (canManage) {
      getLaneImpactMeasures();
    }
  }, [getLaneImpactMeasures, ideas, canManage]);

  const ideaStatusText = useMemo(() => ({
    all: 'All ideas',
    me: 'Ideas assigned to me',
    unassigned: 'Ideas that are unassigned',
  }), []);

  const showIdeaFilters = useMemo(() => ((ideas?.length > 0) || ideaFilter || ideaView || loading) && !usingPropIdeas, [ideaFilter, ideaView, ideas?.length, loading, usingPropIdeas]);
  const IdeaFiltersContent = useMemo(() => (
    showIdeaFilters
      ? (
        <>
          <Input
            icon='search'
            iconPosition='left'
            placeholder={`Filter ${ideasTotal} ideas by name or author...`}
            value={ideaFilter}
            onChange={(e) => setIdeaFilter(e.target.value)}
            style={{
              height: FilterHeight,
              width: 300,
            }}
            size='small'
            loading={loading}
          />
          <TagDropdownChooser
            tags={selectedTags}
            availableTags={availableTags}
            addTag={addTag}
            removeTag={removeTag}
            showCount={false}
            style={{
              marginLeft: 5,
              height: FilterHeight,
              minHeight: 0,
              fontSize: '.92857143em',
            }}
            onStateChange={undefined}
            size='small'
          />
          <Dropdown
            icon={null}
            style={{ marginLeft: 5, height: FilterHeight }}
            direction='left'
            trigger={(
              <Button
                basic
                content={`Showing ${ideaStatusText[ideaView || 'all'].toLowerCase()}`}
                icon='dropdown'
                style={{ height: FilterHeight }}
                size='small'
              />
            )}
          >
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setIdeaView('all')}>Show all ideas</Dropdown.Item>
              <Dropdown.Item onClick={() => setIdeaView('me')} >Show ideas assigned to me</Dropdown.Item>
              <Dropdown.Item onClick={() => setIdeaView('unassigned')} >Show ideas that are unassigned</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </>
      ) : null
  ), [showIdeaFilters, ideasTotal, ideaFilter, loading, selectedTags, availableTags, addTag, removeTag, ideaStatusText, ideaView, setIdeaFilter, setIdeaView]);

  const canSetupLanes = useMemo(() => (canManage && !lanes.length), [canManage, lanes.length]);
  const browserIsSupported = useMemo(() => util.browserIsSupported(), []);
  const showHeaderContent = useMemo(() => (canSetupLanes || !browserIsSupported || !usingPropIdeas), [browserIsSupported, canSetupLanes, usingPropIdeas]);

  const BoardHeaderContent = useMemo(() => (
    <>
      {canSetupLanes
        ? (
          <Button icon='cogs' size='small' content='Setup lanes' onClick={() => setSetupModalOpen(true)} style={{ marginLeft: 5, height: FilterHeight }} />
        ) : null
      }
      {!browserIsSupported && (
        <Message warning icon='info circle'>
          <Message.Header>Unsupported browser</Message.Header>
          <p>{util.appName()} is unable to display the project board because your browser does not support the required features. We recommend updating to a more modern browser, such as a newer version of Edge, Firefox or Chrome.</p>
        </Message>
      )}
      {!usingPropIdeas
        ? (
          <BoardEvents
            forId={forId}
            setCurrentIdeaId={setCurrentIdeaId}
          />
        ) : null
      }
    </>
  ), [browserIsSupported, canSetupLanes, forId, setCurrentIdeaId, setSetupModalOpen, usingPropIdeas]);

  return (
    <StyledProjectBoardContainer>
      {showIdeaFilters || showHeaderContent
        ? (
          <StyledBoard>
            <StyledBoardHeader>
              {theme.sizes.isComputer
                ? (
                  <>
                    <div className='filter-container'>
                      {/* <div className='header'>
                        <Icon name='compass'size='large' style={{ marginRight: 10 }} />
                        <div style={{ maxWidth: !theme.sizes.isMobile && '37vw' }}>{heading}</div>
                      </div> */}
                      <div className='filters'>
                        {IdeaFiltersContent}
                      </div>
                    </div>
                    <div className='filter-container'>
                      <div className='filters'>
                        {BoardHeaderContent}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className='filter-container'>
                    {/* <div className='header'>
                      <Icon name='compass' size="big" style={{ marginRight: 10 }} />
                      <div style={{ maxWidth: !theme.sizes.isMobile && '37vw' }}>{heading}</div>
                    </div> */}
                    <div className='filters'>
                      {IdeaFiltersContent}
                      {BoardHeaderContent}
                    </div>
                  </div>
                )
              }
            </StyledBoardHeader>
          </StyledBoard>
        ) : null
      }
      <StyledLaneContainer>
        {(loading && !ideaFilter && !ideaView && !initialDataLoaded)
          ? (
            <LoadingContainer>
              <Loader active inline size='small' />
              <span>Loading project board...</span>
            </LoadingContainer>
          ) : (
            <>
              {(!ideas?.length && !ideaFilter && !ideaView && !loading && !usingPropIdeas)
                ? (
                  <Message style={{ margin: 10 }} info header='No ideas are on this board yet' content={canManage ? 'Add ideas to this board to manage them as projects.' : 'Come back later to see how ideas are getting on.'} />
                ) : null
              }
              <Board
                lanes={laneData}
                onDragEnd={cardDragged}
                createLane={createLane}
                highlightCard={highlightCard}
                renderLaneHeader={(lane, handle) => {
                  const { cards = [] } = lane;
                  const allIdeasSelected = cards.length > 0 && cards.every((card) => selectedIdeaIds.includes(card.idea._id));
                  return (
                    <CustomLaneHeader
                      lane={lane}
                      handleProps={handle}
                      totalIdeas={totalIdeas}
                      clickLane={laneClicked}
                      user={user}
                      canManage={canManage}
                      laneImpactMeasures={laneImpactMeasures[lane.id] ?? []}
                      laneSettingsProps={props}
                      isSelecting={isMultiSelect}
                      isSelected={allIdeasSelected}
                      onSelect={() => {
                        if (allIdeasSelected) {
                          onSelectIdeas(cards.map((card) => card.idea._id), false);
                        } else {
                          onSelectIdeas(cards.map((card) => card.idea._id), true);
                        }
                      }}
                    />
                  );
                }}
                renderCard={(card, handle) => (
                  <CustomLaneCard
                    card={card}
                    handleProps={handle}
                    user={user}
                    onClick={setCurrentIdeaId}
                    highlightCard={highlightCard}
                    getBoard={getBoard}
                    isChallengeBoard={isChallengeBoard}
                    rewardIdeaId={rewardIdeaId}
                    setRewardIdeaId={setRewardIdeaId}
                    isSelecting={isMultiSelect}
                    onSelect={(ideaId) => onSelectIdeas([ideaId], !selectedIdeaIds.includes(ideaId))}
                    isSelected={selectedIdeaIds.indexOf(card.idea._id) > -1}
                  />
                )}
              />
            </>
          )
        }
      </StyledLaneContainer>
    </StyledProjectBoardContainer>
  );
};

export default ProjectBoard;
